import type { CSSProperties } from 'react';

import { config } from '@/lib/config';

// Shared page chrome + inline line icons for the cardless-editorial chat app.
// No icon library — each glyph is a small SVG matching the design handoff, drawn
// at 1.5–2px stroke in currentColor so the parent (.glyph / pill tone) sets color.

/** WeeBee · Metro wordmark shown in the topbar. */
export function Wordmark() {
  return (
    <div className="wordmark">
      <span className="tx">WeeBee</span>
      <i className="bd" />
      <span className="mt">Metro</span>
    </div>
  );
}

/**
 * openWhatsApp deep-links back to the WeeBee WhatsApp business chat. The app runs
 * inside WhatsApp's in-app browser with no history entry, so history.back() is a
 * no-op and window.close() is blocked on iOS — a wa.me link reliably reopens the
 * chat on both platforms. Every "back to chat" / "open chat" affordance routes here.
 */
export function openWhatsApp() {
  window.location.href = config.whatsappUrl;
}

/** "Back to chat" ghost button — used on error/invalid states. */
export function BackToChat() {
  return (
    <button type="button" className="btn btn--ghost" onClick={openWhatsApp}>
      <IconChevronLeft />
      <span>Back to chat</span>
    </button>
  );
}

/** "Open WeeBee chat" solid (ink) button — used on landing + already-paid/issued states. */
export function OpenChatButton({ label = 'Open WeeBee chat' }: { label?: string }) {
  return (
    <button type="button" className="btn btn--ink" onClick={openWhatsApp}>
      <IconChat />
      <span>{label}</span>
    </button>
  );
}

interface IconProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
}

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor' as const,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

export function IconLock({ size = 11, strokeWidth = 2, className, style }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className} style={style} aria-hidden>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function IconTick({
  size = 11,
  strokeWidth,
  className,
  style,
  circled = false,
}: IconProps & { circled?: boolean }) {
  if (circled) {
    return (
      <svg
        {...base(size)}
        strokeWidth={strokeWidth ?? 1.5}
        className={className}
        style={style}
        aria-hidden
      >
        <circle cx="12" cy="12" r="9.5" />
        <path d="M8 12.3 11 15.3 16.2 9" />
      </svg>
    );
  }
  return (
    <svg
      {...base(size)}
      strokeWidth={strokeWidth ?? 3}
      className={className}
      style={style}
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function IconQrGrid({ size = 34, strokeWidth = 1.4, className, style }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className} style={style} aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3M20 20h.01M17 20v.01M20 14v3" />
    </svg>
  );
}

export function IconWarn({ size = 60, strokeWidth = 1.5, className, style }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className} style={style} aria-hidden>
      <path d="M12 3 2 20h20L12 3Z" />
      <path d="M12 10v4.5" />
      <path d="M12 17.5h.01" />
    </svg>
  );
}

export function IconChevronLeft({ size = 15, strokeWidth = 2, className, style }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className} style={style} aria-hidden>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

export function IconBrokenLink({ size = 60, strokeWidth = 1.5, className, style }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className} style={style} aria-hidden>
      <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
      <path d="M4 4 20 20" />
    </svg>
  );
}

export function IconRefresh({ size = 60, strokeWidth = 1.5, className, style }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className} style={style} aria-hidden>
      <path d="M21 12a9 9 0 1 1-6.2-8.5" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

export function IconChat({ size = 16, strokeWidth = 2, className, style }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className} style={style} aria-hidden>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
    </svg>
  );
}

export function IconWifiOff({ size = 13, strokeWidth = 2, className, style }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className} style={style} aria-hidden>
      <path d="M2 2 22 22" />
      <path d="M5 12.5a10 10 0 0 1 4-2.3" />
      <path d="M8.5 16.4a5 5 0 0 1 3-1.3" />
      <path d="M12 20h.01" />
    </svg>
  );
}

export function IconStruckCircle({ size = 76, strokeWidth = 1.5, className, style }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={strokeWidth} className={className} style={style} aria-hidden>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M5.5 5.5 18.5 18.5" />
    </svg>
  );
}
